// src/components/homeComponents/FAQ.jsx
import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaPlus } from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

/*
  Q&A — THE DIRECTOR'S CUT.
  Not a gray accordion: every question owns an accent color. Opening one
  re-lights the room, sweeps an underline, and the answer arrives with a
  blur-to-sharp reveal. A giant ghost "?" drifts and rotates with scroll.
  NOTE for Amiri: edit questions/answers freely in the ITEMS array.
*/
const ITEMS = [
  {
    q: "How fast can you launch my website?",
    a: "Most sites go live in 2–4 weeks depending on scope. E-commerce with custom animations takes longer than a landing page — but we build in sprints, so you see real progress every few days, not a big reveal at the end.",
    accent: "#9281dd",
  },
  {
    q: "Do you only work with businesses in the Balkans?",
    a: "Our home turf is Kosovo, Albania and Macedonia — we know these markets and their audiences deeply. But everything we do (web, content, ads) works remotely, so we take on brands from anywhere.",
    accent: "#4ea9d6",
  },
  {
    q: "What makes your content different?",
    a: "The hook. Most agencies make ads that look like ads — people scroll past them. We write and shoot content that feels organic and earns attention in the first second. That's how our clients' videos reach millions of views without buying every single eyeball.",
    accent: "#e6608a",
  },
  {
    q: "Do I need both a website and content?",
    a: "They compound. Content captures attention; the website converts it. A great feed pointing to a slow, generic site leaks money — and a beautiful site nobody visits is a museum. We build the full pipeline so neither side wastes the other.",
    accent: "#f2b134",
  },
  {
    q: "How much does it cost?",
    a: "It depends on scope — a landing page and a full e-commerce build with monthly content are very different projects. Tell us what you're trying to grow and we'll give you a clear, fixed quote. No hourly billing surprises.",
    accent: "#8fc15a",
  },
  {
    q: "Who owns everything after we're done?",
    a: "You do. The website, the code, the videos, the designs, the accounts — all of it is yours. We want clients who stay because the work performs, not because they're locked in.",
    accent: "#b9c5fb",
  },
];

const FAQ = () => {
  const sectionRef = useRef(null);
  const ghostRef = useRef(null);
  const auraRef = useRef(null);
  const itemRefs = useRef([]);
  const answerRefs = useRef([]);
  const [open, setOpen] = useState(-1);

  const toggle = (i) => {
    const next = open === i ? -1 : i;

    // close current
    if (open >= 0 && answerRefs.current[open]) {
      gsap.to(answerRefs.current[open], {
        height: 0,
        opacity: 0,
        duration: 0.45,
        ease: "power3.inOut",
      });
    }

    // open next
    if (next >= 0 && answerRefs.current[next]) {
      const el = answerRefs.current[next];
      gsap.set(el, { height: "auto", opacity: 1 });
      const target = el.scrollHeight;
      gsap.fromTo(
        el,
        { height: 0, opacity: 0 },
        { height: target, opacity: 1, duration: 0.55, ease: "power3.out" },
      );
      const inner = el.querySelector("p");
      if (inner) {
        gsap.fromTo(
          inner,
          { y: 18, filter: "blur(6px)", opacity: 0 },
          {
            y: 0,
            filter: "blur(0px)",
            opacity: 1,
            duration: 0.6,
            ease: "power2.out",
            delay: 0.12,
          },
        );
      }
      // room re-light in this question's accent
      if (auraRef.current) {
        gsap.to(auraRef.current, {
          background: `radial-gradient(circle at 30% 40%, ${ITEMS[next].accent}1e 0%, #08040c 65%)`,
          duration: 0.8,
          overwrite: "auto",
        });
      }
    } else if (auraRef.current) {
      gsap.to(auraRef.current, {
        background: `radial-gradient(circle at 30% 40%, #9281dd14 0%, #08040c 65%)`,
        duration: 0.8,
        overwrite: "auto",
      });
    }

    setOpen(next);
  };

  useEffect(() => {
    // ── NOTE: the question items are NEVER touched by JS here. ─────
    // Their entrance cascade is a pure CSS keyframe animation (see
    // the styled-component below) — it always runs to completion, so
    // no scroll math, observer, or pin spacer can ever leave the
    // content stuck invisible. JS only drives the decorative ghost
    // and the click-to-open answers.
    const ctx = gsap.context(() => {
      if (ghostRef.current) {
        gsap.fromTo(
          ghostRef.current,
          { rotate: -14, y: 80 },
          {
            rotate: 10,
            y: -80,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
          },
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef} id="faq">
      <div className="aura" ref={auraRef} />
      <span className="ghost" ref={ghostRef}>
        ?
      </span>

      <div className="head">
        <span className="kicker">Q&A — THE DIRECTOR'S CUT</span>
        <h2>
          Asked. <em>Answered.</em>
        </h2>
      </div>

      <div className="list">
        {ITEMS.map((item, i) => (
          <div
            className={`faq-item ${open === i ? "open" : ""}`}
            key={i}
            ref={(el) => (itemRefs.current[i] = el)}
            style={{
              "--accent": item.accent,
              animationDelay: `${0.1 + i * 0.08}s`,
            }}
          >
            <button className="q" onClick={() => toggle(i)}>
              <span className="no">0{i + 1}</span>
              <span className="text">{item.q}</span>
              <span className="icon">
                <FaPlus />
              </span>
            </button>
            <div className="a" ref={(el) => (answerRefs.current[i] = el)}>
              <p>{item.a}</p>
            </div>
            <span className="line" />
          </div>
        ))}
      </div>

      <div className="still">
        Still curious? <a href="#contact">Ask us directly ↓</a>
      </div>
    </Section>
  );
};

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Section = styled.section`
  position: relative;
  background: #08040c;
  padding: 9em 7% 7em;
  overflow: hidden;

  .aura {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 40%, #9281dd14 0%, #08040c 65%);
    pointer-events: none;
  }

  .ghost {
    position: absolute;
    right: -4%;
    top: 8%;
    font-family: "Readex Pro", sans-serif;
    font-weight: 200;
    font-size: clamp(18rem, 42vw, 38rem);
    line-height: 1;
    color: #ffffff05;
    pointer-events: none;
    user-select: none;
    will-change: transform;
  }

  .head {
    position: relative;
    margin-bottom: 3.5em;
    .kicker {
      font-family: "Montserrat";
      letter-spacing: 0.45em;
      font-size: 0.72rem;
      color: #836caf;
      font-weight: 500;
    }
    h2 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(2.2rem, 5.5vw, 5rem);
      line-height: 0.95;
      margin-top: 0.3em;
      em {
        font-style: italic;
        color: #9281dd;
      }
    }
  }

  .list {
    position: relative;
    max-width: 900px;
  }

  .faq-item {
    position: relative;
    /* pure CSS entrance — always completes, can never strand content
       hidden. Per-item delay is set inline via animationDelay. */
    animation: ${rise} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;

    .q {
      width: 100%;
      display: flex;
      align-items: baseline;
      gap: 1.2em;
      background: none;
      border: none;
      cursor: pointer;
      text-align: left;
      padding: 1.5em 0;
      color: #ffffff;

      .no {
        font-family: "Readex Pro", sans-serif;
        font-weight: 200;
        font-size: 1rem;
        color: #463d6e;
        transition: color 0.4s;
        flex-shrink: 0;
      }
      .text {
        flex: 1;
        font-family: "Readex Pro", sans-serif;
        font-weight: 300;
        font-size: clamp(1.15rem, 2.4vw, 1.8rem);
        line-height: 1.25;
        transition:
          transform 0.4s ease,
          color 0.4s ease;
      }
      .icon {
        flex-shrink: 0;
        color: #6a6080;
        font-size: 0.9rem;
        transition:
          transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
          color 0.4s ease;
        transform: rotate(0deg);
      }
    }

    &:hover .q .text {
      transform: translateX(8px);
      color: var(--accent);
    }
    &:hover .q .no {
      color: var(--accent);
    }

    &.open {
      .q .text {
        color: var(--accent);
      }
      .q .no {
        color: var(--accent);
      }
      .q .icon {
        transform: rotate(135deg);
        color: var(--accent);
      }
      .line {
        transform: scaleX(1);
        background: var(--accent);
      }
    }

    .a {
      height: 0;
      opacity: 0;
      overflow: hidden;
      p {
        margin: 0 0 1.8em;
        padding-left: calc(1rem + 1.2em);
        max-width: 62ch;
        color: #b3aec2;
        font-size: clamp(0.95rem, 1.4vw, 1.1rem);
        line-height: 1.7;
      }
    }

    .line {
      display: block;
      height: 1px;
      width: 100%;
      background: #1d1530;
      transform: scaleX(1);
      transform-origin: left;
      transition: background 0.5s ease;
    }
  }

  .still {
    position: relative;
    margin-top: 3.5em;
    color: #8a8595;
    font-size: 1rem;
    a {
      color: #9281dd;
      text-decoration: none;
      border-bottom: 1px solid #9281dd55;
      transition: 0.3s;
      &:hover {
        color: #b9c5fb;
        border-color: #b9c5fb;
      }
    }
  }

  @media (max-width: 700px) {
    .faq-item .a p {
      padding-left: 0;
    }
  }
`;

export default FAQ;
